import React, { useContext, useEffect, useState } from "react";
import { Alert } from "antd";
import { Error, Success } from "../../../commons/Messages/Messages";
import axios from "axios";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import AddLeaves from "./AddLeaves";
import { SocketContext } from "../../../context/SocketContext";
import { isAuthenticated } from "../../../commons/Auth/auth";
import DropdownComp from "../../../commons/Dropdowns/DropdownComp";
import GeneralTable from "../../../commons/GeneralTable";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import UpdateLeaves from "../../../commons/Employees/EmployeLeaves/UpdateLeaves";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import Tagcomp from "../../../commons/TagComp/Tagcomp";
import dayjs from "dayjs";

import { useTranslation } from "react-i18next";
import PageToolbar from "../../../design-system/PageToolbar";
import { useIsMobile } from "../../../design-system/useMediaQuery";

const Leaves = () => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const socket = useContext(SocketContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [pageSize] = useState(15);

  const getData = async () => {
    setLoading(true);
    await axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/leaves/limited`,
        {
          serviceProvider: isAuthenticated()?.serviceProviderId,
          searchText,
          pageSize,
          pageNo: pageNo - 1
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      )
      .then(res => {
        setLoading(false);
        if (res.status === 200 || res.status === 304) {
          setData(res.data.data);
          setTotalCount(res.data.count);
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        setLoading(false);
        generalErrorAlert(err);
      });
  };

  const deleteHandler = async id => {
    await axios
      .delete(`${process.env.REACT_APP_BACKEND_URL}/leaves/delete/${id}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token")
        }
      })
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);
          getData();
          socket.emit("refresh service provider data", {
            user: isAuthenticated()?.serviceProviderId
          });
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        generalErrorAlert(err);
      });
  };

  const refreshData = () => {
    getData();
  };

  useEffect(() => {
    if (socket) {
      socket.on("refresh service provider data", msg => {
        if (msg) {
          refreshData();
        }
      });
    }
  }, [socket]);

  useEffect(() => {
    getData();

    return () => {};
  }, [pageNo, searchText]);

  // Long reasons/notes: one clipped line in the table (full text on hover), wrapped on phone cards.
  const clipStyle = isMobile
    ? { overflowWrap: "anywhere" }
    : { maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  const columns = [
    {
      title: "#",
      dataIndex: "_id",
      mobile: "hidden",
      render: (text, record, index) => index + 1
    },
    {
      title: t("Employee"),
      dataIndex: "employee",
      mobile: "title",
      sorter: (a, b) => a.employee?.firstName?.localeCompare(b.employee?.firstName),
      render: employee => employee?.firstName + " " + employee?.lastName
    },
    {
      title: t("Leave Type"),
      dataIndex: "leaveType",
      mobile: "subtitle",
      sorter: (a, b) => a.leaveType?.localeCompare(b?.leaveType)
    },
    {
      title: t("Start Date"),
      dataIndex: "startDate",
      render: text => dayjs(text).format("DD/MMM/YYYY") || "N/A"
    },
    {
      title: t("End Date"),
      dataIndex: "endDate",
      render: text => dayjs(text).format("DD/MMM/YYYY") || "N/A"
    },
    {
      title: t("Reason"),
      dataIndex: "reason",
      render: text => (
        <div style={clipStyle} title={text}>
          {text}
        </div>
      )
    },
    {
      title: t("Note"),
      dataIndex: "note",
      render: text => (
        <div style={clipStyle} title={text}>
          {text}
        </div>
      )
    },
    {
      title: t("Status"),
      dataIndex: "status",
      sorter: (a, b) => a.status?.localeCompare(b.status),
      render: (status, obj) => {
        return (
          <div>
            <Tagcomp text={status} />
            {obj?.alert && (
              <Alert
                className="p-2 mt-3"
                type="warning"
                showIcon
                closable
                description={obj?.alert}
              />
            )}
          </div>
        );
      }
    },
    {
      title: t("Action"),
      key: "action",
      align: "right",
      render: (text, obj) => {
        const menuItems = [
          {
            key: "edit",
            component: () => (
              <UpdateLeaves serviceProvider={true} data={obj} updateParent={getData} />
            )
          },
          {
            key: "delete",
            component: () => <DeleteModal text="Leave" onClick={() => deleteHandler(obj?._id)} />
          }
        ];
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  const handleSearch = value => {
    setSearchText(value);
    setPageNo(1);
  };

  return (
    <>
      <PageToolbar
        filters={<SearchBarComp handleSearch={handleSearch} />}
        actions={<AddLeaves updateParent={getData} />}
      />
      <GeneralTable
        pageNo={pageNo}
        pageSize={pageSize}
        totalCount={totalCount}
        data={data}
        columns={columns}
        setPageNo={setPageNo}
        loading={loading}
      />
    </>
  );
};

export default Leaves;
